import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "@phosphor-icons/react";
import { GoogleButton } from "@/components/GoogleButton";
import { LogoMark, LogoFull } from "@/components/Logo";
import { toast } from "sonner";

const LOGIN_BG = "https://static.prod-images.emergentagent.com/jobs/a24e809b-ba6d-486f-ad10-1fdf54c39bbb/images/14ea82833227e344f1c64d68fe2398c4868ef27c4e8f6c122663e95cbd70a05e.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Bienvenido de vuelta");
      navigate(u?.is_super_admin ? "/admin-panel" : "/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al iniciar sesion");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setEmail("demo@ventai.com");
    setPassword("demo1234");
  };

  return (
    <div className="min-h-screen flex bg-[#09090B] text-[#FAFAFA]">
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-[#09090B]">
        {/* Soft radial brand gradient background */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 70% at 25% 20%, rgba(94,106,210,0.22), transparent 60%), radial-gradient(ellipse 70% 60% at 80% 80%, rgba(249,115,22,0.18), transparent 60%), #09090B",
          }}
        />
        {/* Subtle grid texture */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-2.5">
            <LogoMark size={38} />
            <div>
              <div className="font-display font-medium text-lg tracking-tight">
                <span className="text-[#5E6AD2]">Wazco</span> <span className="text-[#F97316]">Express</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Sales · WhatsApp</div>
            </div>
          </Link>
          <div className="flex items-center justify-center flex-1 py-10">
            <LogoFull className="w-full max-w-[460px]" />
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-3xl font-medium tracking-tighter leading-tight mb-3">
              Cierra mas ventas mientras duermes.
            </h1>
            <p className="text-[#A1A1AA] text-sm leading-relaxed font-light">
              IA conversacional 24/7 que atiende, agenda citas, hace seguimiento y cierra ordenes en WhatsApp por ti.
            </p>
            <div className="mt-6 flex items-center gap-4 text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
              <span>GPT-5.2</span>
              <span className="h-1 w-1 rounded-full bg-[#27272A]" />
              <span>WhatsApp Business</span>
              <span className="h-1 w-1 rounded-full bg-[#27272A]" />
              <span>CRM</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm fade-in">
          <div className="mb-10">
            <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-3">Acceder</div>
            <h2 className="font-display text-3xl font-medium tracking-tight">Bienvenido</h2>
            <p className="text-[#A1A1AA] text-sm mt-2">Inicia sesion para continuar.</p>
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#71717A]">Email</Label>
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required
                data-testid="login-email-input"
                className="mt-2 bg-[#121214] border-[#27272A] focus:border-[#5E6AD2] focus:ring-1 focus:ring-[#5E6AD2] h-11"
                placeholder="tu@empresa.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#71717A]">Contrasena</Label>
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required
                autoComplete="current-password"
                data-testid="login-password-input"
                className="mt-2 bg-[#121214] border-[#27272A] focus:border-[#5E6AD2] focus:ring-1 focus:ring-[#5E6AD2] h-11"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-[#5E6AD2] hover:bg-[#4B55C3] text-white"
            >
              {loading ? "Entrando..." : (<><span>Entrar</span><ArrowRight size={16} className="ml-2" /></>)}
            </Button>
            <button
              type="button"
              onClick={fillDemo}
              data-testid="login-fill-demo-button"
              className="w-full h-10 text-xs uppercase tracking-[0.2em] text-[#71717A] hover:text-[#FAFAFA] border border-dashed border-[#27272A] hover:border-[#5E6AD2] rounded-md transition-colors"
            >
              Usar cuenta demo
            </button>
          </form>
          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#27272A]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">o</span>
            <div className="flex-1 h-px bg-[#27272A]" />
          </div>
          <GoogleButton />
          <div className="mt-8 text-sm text-[#A1A1AA] text-center">
            No tienes cuenta?{" "}
            <Link to="/register" className="text-[#5E6AD2] hover:underline" data-testid="link-to-register">Crear cuenta</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
